// Shared list-page keyboard layer (Phase 1.13): j/k move a focus cursor,
// x toggles select, Enter opens, Esc clears. One hook, reused on Tasks,
// Finance (invoices + expenses) and Leads list view — never inside inputs,
// textareas, selects, contenteditable surfaces, or while a modal is open.

import { useEffect, useState } from 'react';

/** True when the event target is a form control or rich-text surface where
    j/k/x/Enter/Esc must be left to normal typing/editing rather than
    hijacked by the list keyboard layer. Duck-types on tagName/
    isContentEditable rather than `instanceof HTMLElement` — a real keydown's
    target always has these, and it keeps this pure/DOM-free so it's
    unit-testable with plain objects (no jsdom dependency needed). */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null || typeof target !== 'object') return false;
  const node = target as { tagName?: string; isContentEditable?: boolean };
  if (node.isContentEditable) return true;
  return node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.tagName === 'SELECT';
}

/** True when Enter must be left to the browser's native activation — the
    user has real DOM focus on a button or link (e.g. tabbed to a bulk-bar
    action, where hijacking Enter used to open the focused row instead of
    pressing the button). Only Enter defers: j/k/x drive the VIRTUAL focus
    cursor and mean nothing to a button, so they keep working. Same
    duck-typing rationale as isTypingTarget. */
export function isNativeActivationTarget(target: EventTarget | null): boolean {
  if (target === null || typeof target !== 'object') return false;
  const node = target as { tagName?: string };
  return node.tagName === 'BUTTON' || node.tagName === 'A';
}

export function useListKeyboard({
  ids,
  enabled,
  onToggle,
  onOpen,
  onClear,
}: {
  /** Ids in on-screen order — j/k walk this list. */
  ids: readonly string[];
  /** false while a modal is open, or the page/tab doesn't support the layer. */
  enabled: boolean;
  onToggle: (id: string) => void;
  /** Enter on the focused row. Omit if the entity has nothing to open. */
  onOpen?: (id: string) => void;
  onClear: () => void;
}): { focusedId: string | null; setFocusedId: (id: string | null) => void } {
  const [focusedId, setFocusedId] = useState<string | null>(null);

  // A row that scrolls out of the visible/filtered set (tab switch, search,
  // a bulk action removing it) can't stay focused.
  useEffect(() => {
    if (focusedId && !ids.includes(focusedId)) setFocusedId(null);
  }, [ids, focusedId]);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      if (e.key === 'Escape') {
        onClear();
        setFocusedId(null);
        return;
      }
      if (ids.length === 0) return;
      const idx = focusedId ? ids.indexOf(focusedId) : -1;
      if (e.key === 'j') {
        e.preventDefault();
        setFocusedId(ids[Math.min(ids.length - 1, idx + 1)]);
      } else if (e.key === 'k') {
        e.preventDefault();
        setFocusedId(ids[Math.max(0, idx === -1 ? 0 : idx - 1)]);
      } else if (e.key === 'x') {
        if (focusedId) {
          e.preventDefault();
          onToggle(focusedId);
        }
      } else if (e.key === 'Enter') {
        if (isNativeActivationTarget(e.target)) return;
        if (focusedId && onOpen) {
          e.preventDefault();
          onOpen(focusedId);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, ids, focusedId, onToggle, onOpen, onClear]);

  return { focusedId, setFocusedId };
}
